import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, CheckCircle2, Plus, ShieldCheck, Trash2 } from 'lucide-react'
import {
  COMPUTATION_METHODS,
  EMPTY_PASS_RULE,
  PASS_MODES,
  type PassRule,
  type RuleVersionInfo,
  type StageInfo,
  type TieBreaker,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Select,
  Textarea,
  useAuth,
  useCan,
  useToast,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import { SELECTION_KEY } from '../../lib/selection'

const options = (o: Record<string, string>) =>
  Object.entries(o).map(([value, label]) => ({ value, label }))
const num = (v: string) => (v.trim() === '' ? null : Number(v.replace(',', '.')))
const str = (v: number | null) => (v == null ? '' : String(v))

/**
 * Règles de passage d'une étape (cahier §9.7 à §9.9) : versionnées ; une
 * version ne s'applique qu'une fois VALIDÉE par une autre personne que son
 * auteur. Une modification après validation exige un motif.
 */
export function RulesPanel({ stage, canConfigure }: { stage: StageInfo; canConfigure: boolean }) {
  const { session, user } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canValidate = useCan('selection.rules.validate', {
    type: 'competition',
    id: stage.competitionId,
  })
  const draft = stage.rules.find((r) => r.status === 'draft') ?? null
  const valid = stage.rules.find((r) => r.status === 'validated') ?? null
  const base = draft?.rules ?? valid?.rules ?? defaults(stage)
  const [rule, setRule] = useState<PassRule>(base)
  const [reason, setReason] = useState('')
  const set = (patch: Partial<PassRule>) => setRule((r) => ({ ...r, ...patch }))
  const editable = canConfigure && stage.status === 'draft'
  const needsReason = Boolean(valid)

  const saveDraft = useMutation({
    mutationFn: () =>
      session.api.put(`/admin/stages/${stage.id}/rules`, {
        rules: rule,
        reason: needsReason ? reason.trim() : null,
      }),
    onSuccess: async () => {
      toast.success('Brouillon des règles enregistré — en attente de validation')
      setReason('')
      await queryClient.invalidateQueries({ queryKey: SELECTION_KEY })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const validate = useMutation({
    mutationFn: (version: number) =>
      session.api.post(`/admin/stages/${stage.id}/rules/${version}/validate`, {}),
    onSuccess: async () => {
      toast.success('Règles validées : elles s’appliqueront au calcul')
      await queryClient.invalidateQueries({ queryKey: SELECTION_KEY })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const thresholdMode = rule.mode === 'threshold' || rule.mode === 'threshold_quota'
  const quotaMode = rule.mode === 'quota' || rule.mode === 'threshold_quota'
  const disciplines = stage.disciplines.map((d) => ({ value: d.code, label: d.label }))

  return (
    <div className="stack" style={{ gap: 'var(--space-4)' }}>
      {draft && (
        <PendingDraft
          draft={draft}
          mine={draft.createdBy?.id === user?.id}
          canValidate={canValidate}
          loading={validate.isPending}
          onValidate={() => validate.mutate(draft.version)}
        />
      )}

      <Card>
        <CardHeader
          title={
            !editable
              ? valid
                ? `Règles applicables — v${valid.version}`
                : 'Règles de passage'
              : draft
                ? `Brouillon v${draft.version}`
                : valid
                  ? 'Modifier les règles'
                  : 'Définir les règles'
          }
        />
        <CardBody>
          <p className="muted" style={{ marginTop: 0 }}>
            {valid && !editable
              ? 'Version validée, appliquée au calcul de l’étape.'
              : valid
                ? `Version applicable : v${valid.version}. Toute modification crée une nouvelle version à faire valider.`
                : 'Ces règles viennent du règlement du concours : aucune n’est prédéfinie.'}
          </p>
          <fieldset
            disabled={!editable}
            className="stack"
            style={{ gap: 'var(--space-4)', border: 0, padding: 0, margin: 0 }}
          >
            <div className="form-grid">
              <Select
                label="Mode de passage"
                required
                value={rule.mode}
                options={options(PASS_MODES).filter((o) =>
                  stage.graded ? o.value !== 'status' : o.value === 'status' || o.value === 'manual'
                )}
                onChange={(e) => set({ mode: e.target.value as PassRule['mode'] })}
              />
              {stage.graded && (
                <Select
                  label="Méthode de calcul"
                  required
                  value={rule.method}
                  options={options(COMPUTATION_METHODS)}
                  onChange={(e) => set({ method: e.target.value as PassRule['method'] })}
                />
              )}
              {thresholdMode && (
                <Input
                  label="Note minimale de passage"
                  required
                  inputMode="decimal"
                  value={str(rule.threshold)}
                  onChange={(e) => set({ threshold: num(e.target.value) })}
                />
              )}
              {quotaMode && (
                <Input
                  label="Nombre de places"
                  required
                  type="number"
                  min={1}
                  value={str(rule.quota)}
                  hint={stage.seats ? `Places de l’étape : ${stage.seats}` : undefined}
                  onChange={(e) => set({ quota: num(e.target.value) })}
                />
              )}
              {stage.graded && (
                <Input
                  label="Moyenne générale éliminatoire"
                  inputMode="decimal"
                  value={str(rule.eliminatoryScore)}
                  hint="Facultatif"
                  onChange={(e) => set({ eliminatoryScore: num(e.target.value) })}
                />
              )}
              {stage.graded && (
                <label className="check-row" style={{ alignSelf: 'center' }}>
                  <input
                    type="checkbox"
                    checked={rule.byCategory}
                    onChange={(e) => set({ byCategory: e.target.checked })}
                  />
                  <span>Classement et places par catégorie</span>
                </label>
              )}
            </div>

            {stage.graded && rule.mode !== 'manual' && (
              <TieBreakers
                editable={editable}
                value={rule.tieBreakers}
                disciplines={disciplines}
                onChange={(tieBreakers) => set({ tieBreakers })}
              />
            )}

            {stage.graded && (
              <div
                className="row"
                style={{ gap: 'var(--space-3)', alignItems: 'end', flexWrap: 'wrap' }}
              >
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={rule.supplementary.enabled}
                    onChange={(e) =>
                      set({
                        supplementary: { enabled: e.target.checked, size: rule.supplementary.size },
                      })
                    }
                  />
                  <span>Liste complémentaire</span>
                </label>
                {rule.supplementary.enabled && (
                  <Input
                    label="Taille de la liste complémentaire"
                    type="number"
                    min={1}
                    value={str(rule.supplementary.size)}
                    onChange={(e) =>
                      set({ supplementary: { enabled: true, size: num(e.target.value) } })
                    }
                  />
                )}
              </div>
            )}

            {needsReason && editable && (
              <Textarea
                label="Motif de la modification"
                required
                rows={2}
                value={reason}
                hint="Obligatoire : une version a déjà été validée (10 caractères minimum)."
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </fieldset>
          {editable && (
            <div
              className="row"
              style={{ marginTop: 'var(--space-4)', justifyContent: 'flex-end' }}
            >
              <Button
                variant="primary"
                loading={saveDraft.isPending}
                disabled={needsReason && reason.trim().length < 10}
                onClick={() => saveDraft.mutate()}
              >
                {draft ? 'Mettre à jour le brouillon' : 'Enregistrer le brouillon'}
              </Button>
            </div>
          )}
          {!editable && stage.status !== 'draft' && (
            <Alert tone="info">Barèmes verrouillés : les règles ne sont plus modifiables.</Alert>
          )}
        </CardBody>
      </Card>

      {stage.rules.length > 0 && <History versions={stage.rules} />}
    </div>
  )
}

function defaults(stage: StageInfo): PassRule {
  return stage.graded ? EMPTY_PASS_RULE : { ...EMPTY_PASS_RULE, mode: 'status' }
}

function PendingDraft({
  draft,
  mine,
  canValidate,
  loading,
  onValidate,
}: {
  draft: RuleVersionInfo
  mine: boolean
  canValidate: boolean
  loading: boolean
  onValidate: () => void
}) {
  return (
    <Alert tone={draft.issues.length ? 'warning' : 'info'}>
      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        <strong>
          Version {draft.version} en attente de validation — préparée par{' '}
          {draft.createdBy?.name ?? '—'} le {when(draft.createdAt)}
        </strong>
        {draft.reason && <span>Motif : {draft.reason}</span>}
        {draft.issues.length > 0 && (
          <ul className="sel-issues">
            {draft.issues.map((i) => (
              <li key={i.field + i.message}>{i.message}</li>
            ))}
          </ul>
        )}
        {canValidate && !mine && (
          <div>
            <Button
              variant="primary"
              size="sm"
              icon={<ShieldCheck />}
              loading={loading}
              disabled={draft.issues.length > 0}
              onClick={onValidate}
            >
              Valider la version {draft.version}
            </Button>
          </div>
        )}
        {canValidate && mine && (
          <span className="muted">
            Vous êtes l’auteur de cette version : une autre personne doit la valider.
          </span>
        )}
      </div>
    </Alert>
  )
}

function TieBreakers({
  editable,
  value,
  disciplines,
  onChange,
}: {
  editable: boolean
  value: TieBreaker[]
  disciplines: { value: string; label: string }[]
  onChange: (v: TieBreaker[]) => void
}) {
  const move = (i: number, d: -1 | 1) => {
    const next = [...value]
    const [item] = next.splice(i, 1)
    next.splice(i + d, 0, item!)
    onChange(next)
  }
  return (
    <fieldset className="stack" style={{ gap: 'var(--space-2)', border: 0, padding: 0, margin: 0 }}>
      <legend className="field-label">Départage des ex æquo (dans l’ordre)</legend>
      {value.length === 0 && (
        <p className="muted" style={{ margin: 0 }}>
          Aucune règle : les égalités restantes seront soumises à une décision humaine.
        </p>
      )}
      {value.map((t, i) => (
        <div
          key={i}
          className="row"
          style={{ gap: 'var(--space-2)', alignItems: 'end', flexWrap: 'wrap' }}
        >
          <span className="sel-rank">{i + 1}</span>
          {t.kind === 'discipline' ? (
            <Select
              label="Meilleure note en"
              value={t.discipline}
              placeholder="— Discipline —"
              options={disciplines}
              onChange={(e) =>
                onChange(
                  value.map((x, j) =>
                    j === i ? { kind: 'discipline', discipline: e.target.value } : x
                  )
                )
              }
            />
          ) : (
            <Select
              label="Âge"
              value={t.order}
              options={[
                { value: 'younger', label: 'Le plus jeune passe devant' },
                { value: 'older', label: 'Le plus âgé passe devant' },
              ]}
              onChange={(e) =>
                onChange(
                  value.map((x, j) =>
                    j === i ? { kind: 'age', order: e.target.value as 'younger' | 'older' } : x
                  )
                )
              }
            />
          )}
          {editable && (
            <>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                icon={<ArrowUp />}
                aria-label="Monter"
                disabled={i === 0}
                onClick={() => move(i, -1)}
              />
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                icon={<ArrowDown />}
                aria-label="Descendre"
                disabled={i === value.length - 1}
                onClick={() => move(i, 1)}
              />
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                icon={<Trash2 />}
                aria-label={`Retirer le critère ${i + 1}`}
                onClick={() => onChange(value.filter((_, j) => j !== i))}
              />
            </>
          )}
        </div>
      ))}
      {editable && (
        <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Button
            size="sm"
            icon={<Plus />}
            disabled={value.length >= 6 || disciplines.length === 0}
            onClick={() =>
              onChange([...value, { kind: 'discipline', discipline: disciplines[0]?.value ?? '' }])
            }
          >
            Critère : discipline
          </Button>
          <Button
            size="sm"
            icon={<Plus />}
            disabled={value.length >= 6 || value.some((t) => t.kind === 'age')}
            onClick={() => onChange([...value, { kind: 'age', order: 'younger' }])}
          >
            Critère : âge
          </Button>
        </div>
      )}
    </fieldset>
  )
}

function History({ versions }: { versions: RuleVersionInfo[] }) {
  return (
    <Card>
      <CardHeader title="Historique des versions" />
      <CardBody>
        <ol className="sel-history">
          {[...versions]
            .sort((a, b) => b.version - a.version)
            .map((v) => (
              <li key={v.id}>
                <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                  <strong>v{v.version}</strong>
                  <Badge
                    variant={
                      v.status === 'validated'
                        ? 'success'
                        : v.status === 'draft'
                          ? 'warning'
                          : 'neutral'
                    }
                  >
                    {v.status === 'validated' && <CheckCircle2 aria-hidden="true" size={12} />}{' '}
                    {v.statusLabel}
                  </Badge>
                  <span className="muted">{PASS_MODES[v.rules.mode]}</span>
                </div>
                <div className="muted sel-history-meta">
                  Préparée par {v.createdBy?.name ?? '—'} le {when(v.createdAt)}
                  {v.validatedBy &&
                    ` · validée par ${v.validatedBy.name} le ${when(v.validatedAt)}`}
                </div>
                {v.reason && <div className="sel-history-meta">Motif : {v.reason}</div>}
              </li>
            ))}
        </ol>
      </CardBody>
    </Card>
  )
}
